import Cookies from 'cookies';
import { ObjectId } from 'mongodb';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { useRouter } from 'next/router';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';

import { getAd, getCountAds } from '@/api/fetch/marketplace-api';
import { getUser } from '@/api/fetch/rest-marketplace-api';
import { AdByIDRequest } from '@/api/gen-protos/ads_pb';
import { APP_CONFIG } from '@/app-config';
import { LIMIT } from '@/lib/constants';
import { categories } from '@/lib/helpers';
import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';
import ListingItemView from '@/modules/core/components/ListingItemView/ListingItemView';
import SideGallery from '@/modules/core/components/SideGallery';
import {
  mapAdResponseToDetails,
  mapUserResponseToIUserData,
} from '@/modules/core/mappings';
import { IAdDetailItem, IUserData, ListingState } from '@/modules/core/types';
import { Meta } from '@/seo/Meta';
import Text from '@/tayara-kit/Text';

type ListingItemPageProps =
  | ListingItemPageActiveProps
  | ListingItemPageDeletedProps;

interface ListingItemPageDeletedProps extends SSRConfig {
  adDeleted: ListingState;
}

interface ListingItemPageActiveProps extends SSRConfig {
  adDetails: IAdDetailItem;
  adUserData: IUserData;
  countAdByUser: number;
}

const ListingItemPage = (props: ListingItemPageProps) => {
  const router = useRouter();
  const { adDetails, adUserData, countAdByUser } =
    props as ListingItemPageActiveProps;

  if ((props as ListingItemPageDeletedProps).adDeleted !== undefined) {
    const state = (() => {
      switch ((props as ListingItemPageDeletedProps).adDeleted) {
        case ListingState.PENDING_MANUAL_MOD || ListingState.PENDING_MOD:
          return 'en modération';
        case ListingState.SOLD:
          return 'vendue';
        default:
          return 'supprimée';
      }
    })();

    return (
      <>
        <Meta
          title={`Annonce ${state} | ${APP_CONFIG.site_name}`}
          description={`Annonce ${state} sur ${APP_CONFIG.site_name}`}
          canonical={`${APP_CONFIG.basePath}${router.asPath}`}
        />
        <div className="mt-32 flex items-center flex-col text-center px-6">
          <Text
            as="h1"
            text={`Annonce ${state} ¯\\_(ツ)_/¯`}
            className="text-2xl font-bold text-gray-700 font-arabic mb-6"
          />
          <img
            src="/media/undraw_gone.svg"
            alt="No results found for your search on tayara"
            className="h-full max-h-screen w-full max-w-md"
          />
        </div>
      </>
    );
  }

  if (!adDetails) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': (() => {
      if (
        adDetails.category === 'Immobilier' &&
        adDetails.subCategoryId === '60be84bd50ab95b45b08a09d'
      )
        return 'House';
      if (
        adDetails.category === 'Véhicules' &&
        adDetails.subCategoryId === '60be84be50ab95b45b08a0a4'
      )
        return 'Car';
      if (
        adDetails.category === 'Véhicules' &&
        adDetails.subCategoryId === '60be84be50ab95b45b08a0a4'
      )
        return 'Car';
      if (adDetails.category === 'Véhicules') return 'Vehicle';
      return 'Product';
    })(),
    name: adDetails!.title,
    description: adDetails!.description,
    image: adDetails.images[0]
      ? adDetails.images[0].includes('http')
        ? `https://www.tayara.tn/mediaGateway/resize-image?img=${adDetails.images[0].substring(
            adDetails.images[0].lastIndexOf('/') - 2
          )}&w=300`
        : `https://www.tayara.tn/mediaGateway/resize-image?img=${adDetails.images[0]}&w=300`
      : undefined,
    author: [
      {
        '@type': adDetails.publisher.isShop ? 'Organization' : 'Person',
        name: adDetails.publisher.name,
        logo: adDetails.publisher.isShop
          ? adDetails.publisher.avatar
          : undefined,
      },
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: adDetails.location?.governorate,
      addressRegion: adDetails.location?.delegation,
    },
    numberOfRooms: adDetails.adParams?.find((o) => o.label === 'Chambres')
      ?.value,
    floorSize: adDetails.adParams?.find((o) => o.label === 'Superficie')
      ? {
          '@type': 'QuantitativeValue',
          value: adDetails.adParams?.find((o) => o.label === 'Superficie')
            ?.value,
          unitCode: 'FTQ',
        }
      : undefined,

    bodyType: adDetails.adParams?.find((o) => o.label === 'Type de carrosserie')
      ?.value,
    brand: adDetails.adParams?.find((o) => o.label === 'Marque')
      ? {
          '@type': 'Brand',
          name: adDetails.adParams?.find((o) => o.label === 'Marque')?.value,
        }
      : undefined,
    model: adDetails.adParams?.find((o) => o.label === 'Modèle')
      ? {
          '@type': 'ProductModel',
          name: adDetails.adParams?.find((o) => o.label === 'Modèle')?.value,
        }
      : undefined,
    fuelType: adDetails.adParams?.find((o) => o.label === 'Carburant')?.value,
    mileageFromOdometer: adDetails.adParams?.find(
      (o) => o.label === 'Kilométrage'
    )?.value,
    vehicleTransmission: adDetails.adParams?.find((o) => o.label === 'Boite')
      ?.value,

    offers:
      adDetails.price > 1
        ? {
            '@type': 'Offer',
            priceCurrency: 'TND',
            price: adDetails.price,
            acceptedPaymentMethod: 'Cash',
          }
        : undefined,
    datePublished: adDetails.publishedOn,
  };

  return (
    <>
      <Meta
        title={`${adDetails!.title} | ${APP_CONFIG.site_name}`}
        description={`${adDetails!.title} sur ${APP_CONFIG.site_name}`}
        canonical={`${APP_CONFIG.basePath}${router.asPath}`}
      />
      <script
        key="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schema),
        }}
      />
      <NextSeo
        openGraph={{
          type: 'website',
          url: APP_CONFIG.basePath + router.asPath,
          title: `${adDetails.title} | tayara.tn`,
          description: `${adDetails.description} | tayara.tn`,
          images: [
            ...adDetails.images.map((img) => ({
              url: img.includes('http')
                ? `https://www.tayara.tn/mediaGateway/resize-image?img=${img.substring(
                    img.lastIndexOf('/') - 2
                  )}&w=300`
                : `https://www.tayara.tn/mediaGateway/resize-image?img=${img}&w=300`,
            })),
          ],
        }}
      />
      <ListingItemView
        adDetails={adDetails!}
        userDetails={adUserData!}
        countAdByUser={countAdByUser}
      />
    </>
  );
};

export default ListingItemPage;

// Layout configuration
ListingItemPage.getLayout = function getLayout(
  page: JSX.Element,
  props: ListingItemPageProps
) {
  return (
    <Layout>
      {(props as ListingItemPageDeletedProps).adDeleted === undefined ? (
        <Layout.WithSidePanel
          aside={
            <SideGallery
              images={
                (props as ListingItemPageActiveProps).adDetails?.images || []
              }
            />
          }
        >
          {page}
        </Layout.WithSidePanel>
      ) : (
        page
      )}
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
  req,
  res,
}): Promise<GetServerSidePropsResult<ListingItemPageProps>> => {
  const localeData = await serverSideTranslations(locale || 'en');

  if (!Array.isArray(query?.slug) || !query?.slug?.length) {
    res.statusCode = 400;
    return { notFound: true };
  }

  const slugArray = query.slug as string[];
  const lastPart = slugArray[slugArray.length - 1] || '';
  const firstPart = slugArray[0];

  function isValidObjectId(id: string): boolean {
    return ObjectId.isValid(id) && String(new ObjectId(id)) === id;
  }

  // --- Handle old URL redirect ---
  if (!isValidObjectId(lastPart)) {
    const id = firstPart;

    // Build new slug (skip the id at start, keep rest of slug parts)
    const newSlug = slugArray
      .slice(1) // everything except first element (id)
      .map((part) => part.toLowerCase().replace(/\s+/g, '-'))
      .join('/');

    const newUrl = `/item/${newSlug}/${id}`;

    return {
      redirect: {
        destination: newUrl,
        permanent: true,
      },
    };
  }

  // --- Normal flow with valid ObjectId ---
  const adByIDRequest = new AdByIDRequest();
  adByIDRequest.setId(lastPart);

  let ad;
  try {
    ad = await getAd(adByIDRequest);
  } catch (e) {
    res.statusCode = 404;
    return { notFound: true };
  }

  const [user, countAdByUser] = await Promise.all([
    getUser(ad.getAd()?.getUserid() || null),
    getCountAds({
      isPublic: true,
      limit: LIMIT,
      skip: 0,
      userId: ad.getAd()?.getUserid() || '',
      status: 2,
    }),
  ]);

  const iAdDetailItem = mapAdResponseToDetails(ad);

  const cookies = new Cookies(req, res);
  const reqUserId = cookies.get('userid');
  const adOwnerUserId = ad.getAd()?.getUserid();
  const authorizedToViewAd = reqUserId === adOwnerUserId;

  if (
    ![ListingState.ACTIVE, ListingState.PENDING_INSERTION].includes(
      iAdDetailItem.state
    ) &&
    !authorizedToViewAd
  ) {
    const cat = slugArray[0] || '';
    const findSubCategory = categories?.find(
      (c) => c.dbvalue === cat || c.name === cat
    );
    const findCategory = categories?.find(
      (c) => c?.id === findSubCategory?.parentcategoryxid
    )?.name;

    const url = `${findCategory}/${findSubCategory?.name}`;
    res.writeHead(302, {
      Location: `/ads/c/${url}/`,
    });
    res.end();

    return {
      props: {
        ...localeData,
        adDeleted: iAdDetailItem.state,
      },
    };
  }

  return {
    props: cleanObject({
      ...localeData,
      adDetails: iAdDetailItem,
      adUserData: mapUserResponseToIUserData(user),
      countAdByUser: countAdByUser.getCount(),
    }),
  };
};
